import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiAlertCircle, FiCalendar, FiDollarSign, FiFileText, FiUser, FiUpload, FiFile, FiXCircle } from 'react-icons/fi';
import Select from 'react-select';
import { get, viewFile as openStoredFileUrl } from '../services/api';

/** Same set as server EMPLOYEE_DOC_TYPES — vendor field holds employee name */
const EMPLOYEE_DOC_TYPES = ['Civil ID', 'OHC', 'Passport', 'Residence Visa', 'Work Permit'];
/** Align with server normalizeContractType — fixes dropdown + employee UI when DB has "ohc", etc. */
function normalizeContractTypeClient(typeRaw) {
  if (typeRaw === undefined || typeRaw === null || String(typeRaw).trim() === '') return 'Contract';
  const t = String(typeRaw).normalize('NFKC').trim().toLowerCase();
  const collapsed = t.replace(/[\s._-]+/g, '');
  if (collapsed === 'license') return 'License';
  if (collapsed === 'policy') return 'Policy';
  if (collapsed === 'civilid') return 'Civil ID';
  if (collapsed === 'ohc') return 'OHC';
  if (collapsed === 'passport' || collapsed === 'passports') return 'Passport';
  if (collapsed === 'residencevisa' || collapsed === 'residencevisas') return 'Residence Visa';
  if (collapsed === 'workpermit' || collapsed === 'workpermits') return 'Work Permit';
  if (collapsed === 'wp') return 'Work Permit';
  if (collapsed === 'rv') return 'Residence Visa';
  return 'Contract';
}

const isEmployeeDocType = (t) =>
  EMPLOYEE_DOC_TYPES.includes(normalizeContractTypeClient(t));

/** Single-line text for FormData (newlines in vendor/name break multipart) */
function sanitizeOneLine(v) {
  if (v == null) return '';
  return String(v).replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Accept real File and file-like blobs across browser contexts */
function isFileLike(value) {
  if (!value) return false;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (
    typeof Blob !== 'undefined' &&
    value instanceof Blob &&
    typeof value.name === 'string' &&
    typeof value.size === 'number'
  ) {
    return true;
  }
  return (
    typeof value === 'object' &&
    typeof value.name === 'string' &&
    typeof value.size === 'number' &&
    (typeof value.arrayBuffer === 'function' || typeof value.stream === 'function')
  );
}

/** Employee full name is stored in vendor on the server */
function resolveEmployeeVendorName(row) {
  if (!row || typeof row !== 'object') return '';
  const v = row.vendor ?? row.VENDOR ?? row.employee_name ?? row.employeeName;
  if (v != null && String(v).trim() !== '') return String(v).trim();
  return '';
}

/** Module-level options so effects always see a stable list (same order as react-select options) */
const ALERT_DAY_OPTIONS = [
  { value: '1', label: '1 day' },
  { value: '3', label: '3 days' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '365 days' },
];

/** Ensure upload paths work with window.open / fetch (leading slash, forward slashes) */
function normalizeStoredAttachmentRef(raw) {
  if (raw == null || raw === false) return '';
  const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return '';
  const norm = s.replace(/\\/g, '/');
  const lower = norm.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return norm;
  if (lower.includes('/uploads/')) return norm.startsWith('/') ? norm : `/${norm.replace(/^\.+\//, '')}`;
  if (/^\/?uploads\//i.test(norm)) return norm.startsWith('/') ? norm : `/${norm}`;
  return norm;
}

/** Normalize stored attachment path from API (snake_case / camelCase / edge drivers) */
function resolveContractFileUrl(contract) {
  if (!contract || typeof contract !== 'object') return '';
  const tryKeys = [
    contract.file_url,
    contract.fileUrl,
    contract.FILE_URL,
    contract.fileURL,
  ];
  for (const raw of tryKeys) {
    if (raw == null || raw === false) continue;
    const s = typeof raw === 'string' ? raw.trim() : String(raw).trim();
    if (s) return normalizeStoredAttachmentRef(s);
  }
  // Try every *file*+*url* column — .find() on keys alone could hit an empty column first and hide file_url
  const dynamicKeys = Object.keys(contract).filter((k) => /file/i.test(k) && /url/i.test(k));
  dynamicKeys.sort((a, b) => {
    const pri = (x) => (/^file_url$/i.test(x) ? 0 : /^fileUrl$/i.test(x) ? 1 : 2);
    return pri(a) - pri(b) || a.localeCompare(b);
  });
  for (const dk of dynamicKeys) {
    const val = contract[dk];
    if (val == null || val === false) continue;
    const s = String(val).trim();
    if (s) return normalizeStoredAttachmentRef(s);
  }
  // Last resort: any field value that looks like an uploads path (imports / odd column names)
  for (const [, val] of Object.entries(contract)) {
    if (val == null || val === false) continue;
    const s = typeof val === 'string' ? val.trim() : String(val).trim();
    if (!s) continue;
    const norm = s.replace(/\\/g, '/').toLowerCase();
    if (norm.includes('/uploads/') || /^\.?\/?uploads\//i.test(norm)) {
      return normalizeStoredAttachmentRef(s);
    }
  }
  return '';
}

/** Prefer payload from props (authoritative on edit open); state only fills gaps / respects removal */
function getEffectiveServerAttachmentUrl(contract, currentFileUrl, fileRemoved) {
  if (fileRemoved) return '';
  const fromContract = resolveContractFileUrl(contract);
  if (fromContract) return fromContract;
  const trimmed = currentFileUrl != null ? String(currentFileUrl).trim() : '';
  return trimmed ? normalizeStoredAttachmentRef(trimmed) : '';
}

const AddContractModal = ({ isOpen, onClose, onSave, contract, user, departments = [], contracts = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    vendor: '',
    vendorContact: '',
    licenseNumber: '',
    startDate: '',
    endDate: '',
    value: '',
    currency: 'KD',
    fileRemoved: false,
  });
  const [documentType, setDocumentType] = useState('Contract');
  const [region, setRegion] = useState('');
  const [alertDays, setAlertDays] = useState(() => [ALERT_DAY_OPTIONS[8]]); // default 365 days
  const [employeeName, setEmployeeName] = useState('');

  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentFileUrl, setCurrentFileUrl] = useState('');
  // Track the contract ID that was last used to initialise the form so the useEffect
  // can skip redundant re-initialisations (avoids resetting the selected File when
  // parent re-renders pass a new object reference for the same contract).
  const lastInitContractIdRef = useRef(null);
  const lastInitIsOpenRef = useRef(false);
  // Treat superadmin like admin for contract creation (can choose department)
  const isAdmin = user && (
    user.role === 'admin' ||
    user.role_name === 'admin' ||
    user.role === 'superadmin' ||
    user.role_name === 'superadmin'
  );
  const [departmentId, setDepartmentId] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [isArchived, setIsArchived] = useState(false);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);

  // Debug: Log departments when they change
  useEffect(() => {
    if (isOpen && departments.length > 0) {
      console.log('AddContractModal - Departments loaded:', departments);
      console.log('Departments with emails:', departments.map(d => ({ id: d.id, name: d.name, email: d.email })));
    }
  }, [departments, isOpen]);

  // (Notification email is now always entered manually; no auto-fill from department)

  // Extract unique vendors from contracts for autocomplete
  useEffect(() => {
    if (contracts && contracts.length > 0) {
      const uniqueVendors = Array.from(new Set(
        contracts
          .map(c => c.vendor)
          .filter(Boolean)
          .map(v => v.trim())
      )).sort();
      setVendorSuggestions(uniqueVendors);
    }
  }, [contracts]);

  // Compute allowed document types based on user permissions
  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const canCreateContracts = isAdmin || userPermissions.includes('contracts:create');
  // Show each type if user has create or read permission (backend enforces create on submit)
  const canCreateLicenses = isAdmin || userPermissions.includes('licenses:create') || userPermissions.includes('licenses:read');
  const canCreatePolicies = isAdmin || userPermissions.includes('policies:create') || userPermissions.includes('policies:read');
  const canCreateCivilIds = isAdmin || userPermissions.includes('civilid:create') || userPermissions.includes('civilid:read');

  let allowedDocumentTypes = [];
  if (canCreateContracts) allowedDocumentTypes.push('Contract');
  if (canCreateLicenses) allowedDocumentTypes.push('License');
  if (canCreatePolicies) allowedDocumentTypes.push('Policy');
  if (canCreateCivilIds) allowedDocumentTypes.push('Civil ID');
  if (canCreateCivilIds || canCreateContracts) {
    ['OHC', 'Passport', 'Residence Visa', 'Work Permit'].forEach((t) => {
      if (!allowedDocumentTypes.includes(t)) allowedDocumentTypes.push(t);
    });
  }

  // When editing, always include the existing type so it remains visible (normalize for comparison)
  const contractTypeNorm = contract?.type ? normalizeContractTypeClient(contract.type) : '';
  const existingTypeForList = contract?.type && EMPLOYEE_DOC_TYPES.includes(contractTypeNorm)
    ? contractTypeNorm
    : contract?.type;
  if (existingTypeForList && !allowedDocumentTypes.includes(existingTypeForList)) {
    allowedDocumentTypes = [existingTypeForList, ...allowedDocumentTypes];
  }

  useEffect(() => {
    if (!isOpen) {
      // Drop in-memory File object when modal closes (URL is set again when opening add/edit)
      setFile(null);
      setCurrentFileUrl('');
      lastInitContractIdRef.current = null;
      lastInitIsOpenRef.current = false;
      return;
    }

    // Guard: skip re-initialisation if the modal is already open for the SAME contract.
    // This prevents the selected File from being wiped when the parent re-renders and
    // passes a new object reference for the same contract (same id) or when other deps
    // like `departments` or `user` update their reference without changing their content.
    const incomingContractId = contract?.id ?? null;
    const alreadyInitialisedForThisContract =
      lastInitIsOpenRef.current === true &&
      lastInitContractIdRef.current === incomingContractId;
    if (alreadyInitialisedForThisContract) {
      return;
    }
    lastInitIsOpenRef.current = true;
    lastInitContractIdRef.current = incomingContractId;

    // Modal open: reset in-memory file pick; then load add vs edit
    setFile(null);
    setErrors({});

    if (contract) {
        // Pre-fill form with contract data for editing
        const normType = normalizeContractTypeClient(contract.type || '');
        const vendorForEmp = resolveEmployeeVendorName(contract);
        setFormData({
          name: contract.name || '',
          description: contract.description || '',
          vendor: isEmployeeDocType(normType) ? (vendorForEmp || '') : (contract.vendor || ''),
          vendorContact: contract.vendor_contact || contract.vendorContact || '',
          licenseNumber: contract.license_number || contract.licenseNumber || '',
          startDate: contract.startDate
            ? new Date(contract.startDate).toISOString().split('T')[0]
            : contract.start_date
              ? new Date(contract.start_date).toISOString().split('T')[0]
              : '',
          endDate: contract.endDate
            ? new Date(contract.endDate).toISOString().split('T')[0]
            : contract.end_date
              ? new Date(contract.end_date).toISOString().split('T')[0]
              : '',
          value: contract.value ? contract.value.toString() : '',
          currency: contract.currency || 'KD',
          fileRemoved: false,
        });
        // Parse alertDays as array of options
        let alertArr = [];
        if (contract.alertDays) {
          if (Array.isArray(contract.alertDays)) {
            alertArr = contract.alertDays.map(val => {
              const found = ALERT_DAY_OPTIONS.find(opt => opt.value === String(val));
              return found || { value: String(val), label: `${val} days` };
            });
          } else if (typeof contract.alertDays === 'string') {
            alertArr = contract.alertDays.split(',').map(s => s.trim()).filter(Boolean).map(val => {
              const found = ALERT_DAY_OPTIONS.find(opt => opt.value === val);
              return found || { value: val, label: `${val} days` };
            });
          } else {
            alertArr = [{ value: String(contract.alertDays), label: `${contract.alertDays} days` }];
          }
        } else if (contract.alert_days) {
          alertArr = String(contract.alert_days).split(',').map(s => s.trim()).filter(Boolean).map(val => {
            const found = ALERT_DAY_OPTIONS.find(opt => opt.value === val);
            return found || { value: val, label: `${val} days` };
          });
        } else {
          alertArr = [ALERT_DAY_OPTIONS[8]]; // 365 days default
        }
        setAlertDays(alertArr);
        const rawDeptId = contract.department_id ?? contract.departmentId;
        setDepartmentId(
          rawDeptId !== undefined && rawDeptId !== null && rawDeptId !== ''
            ? String(rawDeptId)
            : ''
        );
        setCurrentFileUrl(resolveContractFileUrl(contract));
        setNotificationEmail(contract.notification_email || '');
        setIsArchived(contract.status === 'Inactive');
        setDocumentType(normType);
        setRegion(contract.region || '');
        if (isEmployeeDocType(normType)) {
          setEmployeeName(vendorForEmp);
        } else {
          setEmployeeName('');
        }
    } else {
        // Reset form for new contract (no existing server attachment)
        setCurrentFileUrl('');
        setFormData({
          name: '',
          description: '',
          vendor: '',
          vendorContact: '',
          licenseNumber: '',
          startDate: '',
          endDate: '',
          value: '',
          currency: 'KD',
          fileRemoved: false,
        });
        setAlertDays([ALERT_DAY_OPTIONS[8]]); // 365 days default
        setEmployeeName('');

        // For non-admin users, set their department ID automatically
        // Try multiple possible field names for department_id
        const userDepartmentId = user?.department_id ||
          user?.departmentId ||
          user?.department_id ||
          '';
        console.log('Setting up new contract form:', {
          isAdmin,
          user,
          userDepartmentId,
          userKeys: Object.keys(user || {}),
          departmentsCount: departments.length,
          departments: departments.map(d => ({ id: d.id, name: d.name }))
        });

        if (!isAdmin) {
          if (userDepartmentId) {
            // Non-admin user: set their department automatically
            setDepartmentId(String(userDepartmentId));
            console.log('Non-admin user - auto-setting department ID:', userDepartmentId);
          } else {
            console.warn('⚠ Non-admin user but no department_id found in user object.');
            console.warn('Attempting to fetch user department from /api/auth/me endpoint...');
            // Try to fetch the current user's data from /api/auth/me endpoint
            get('/api/auth/me').then(userData => {
              const deptId = userData?.department_id || userData?.departmentId;
              if (deptId) {
                console.log('✓ Found user department from /api/auth/me:', deptId);
                setDepartmentId(String(deptId));
              } else {
                console.error('❌ Could not find user department. Please ensure:');
                console.error('1. Backend server has been restarted after code changes');
                console.error('2. User has a department_id set in the database');
                console.error('3. Check backend logs for department_id in login/me response');
                console.error('User data from /api/auth/me:', userData);
                setDepartmentId('');
              }
            }).catch(err => {
              console.error('Error fetching user data from /api/auth/me:', err);
              setDepartmentId('');
            });
          }
        } else {
          // Admin / superadmin: no default department or email; user chooses both
          setDepartmentId('');
          setNotificationEmail('');
        }

        setIsArchived(false);
        setDocumentType('Contract');
        setRegion('');
    }
  }, [isOpen, contract, contract?.id, user, isAdmin, departments]);

  const validateForm = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.name.trim()) {
      if (documentType === 'Policy') {
        newErrors.name = 'Policy name is required';
      } else {
        newErrors.name = 'Document name is required';
      }
    }
    // Only require vendor/issuer if document type is not an employee-linked type
    if (!isEmployeeDocType(documentType) && !formData.vendor.trim()) {
      if (documentType === 'Policy') {
        newErrors.vendor = 'Issuer name is required';
      } else {
        newErrors.vendor = 'Vendor name is required';
      }
    }

    // Require license number when document type is License
    if (documentType === 'License' && !formData.licenseNumber.trim()) {
      newErrors.licenseNumber = 'License number is required';
    }
    // Require employee name for Civil ID, OHC, Passport, etc.
    if (isEmployeeDocType(documentType) && !employeeName.trim()) {
      newErrors.employeeName = 'Full name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = documentType === 'Policy'
        ? 'Issuance date is required'
        : 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = documentType === 'Policy'
        ? 'Policy end date is required'
        : 'End date is required';
    } else if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = documentType === 'Policy'
        ? 'Policy end date must be after issuance date'
        : 'End date must be after start date';
    }

    // Validate value only when value field is shown (non-Policy)
    if (documentType !== 'Policy' && formData.value && isNaN(parseFloat(formData.value))) {
      newErrors.value = 'Value must be a valid number';
    }

    if (alertDays.length === 0) {
      newErrors.alertDays = 'At least one alert day is required';
    }

    // Validate region - required
    if (!region || !region.trim()) {
      newErrors.region = 'Region is required';
    }

    // Validate file attachment - required for new contracts, or if editing and file was removed
    if (!contract) {
      // New contract - file is required
      if (!isFileLike(file)) {
        newErrors.file = 'Contract document is required';
      }
    } else {
      const hasExisting = !!getEffectiveServerAttachmentUrl(
        contract,
        currentFileUrl,
        !!formData.fileRemoved
      );
      if (!hasExisting && !isFileLike(file)) {
        newErrors.file = 'Contract document is required';
      }
    }

    // Validate notification email(s)
    if (!notificationEmail || !notificationEmail.trim()) {
      newErrors.notificationEmail = 'At least one notification email is required';
    } else {
      // Validate each email address
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emails = notificationEmail.split(',').map(e => e.trim()).filter(Boolean);
      if (emails.length === 0) {
        newErrors.notificationEmail = 'At least one valid email is required';
      } else {
        const invalidEmails = emails.filter(email => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
          newErrors.notificationEmail = `Invalid email format: ${invalidEmails.join(', ')}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Debug: inspect file state before building FormData
    console.log('[handleSubmit] file state:', file, '| isFileLike:', isFileLike(file), '| type:', typeof file);
    console.log('[handleSubmit] contract (edit mode):', !!contract, '| fileRemoved:', formData.fileRemoved);
    console.log('[handleSubmit] currentFileUrl:', currentFileUrl);
    console.log('[handleSubmit] effectiveServerAttachmentUrl:', getEffectiveServerAttachmentUrl(contract, currentFileUrl, !!formData.fileRemoved));

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Add all form data to FormData, mapping camelCase to snake_case for backend
      if (formData.name) formDataToSend.append('name', sanitizeOneLine(formData.name));
      // Only add vendor fields if not employee-linked document type
      if (!isEmployeeDocType(documentType)) {
        if (formData.vendor) formDataToSend.append('vendor', sanitizeOneLine(formData.vendor));
        if (formData.vendorContact) formDataToSend.append('vendor_contact', sanitizeOneLine(formData.vendorContact));
      } else {
        // Employee name sent as vendor for HR / personal document types.
        // Always append (even empty string) when editing so the server knows the current value;
        // validation above already blocks submit if employeeName is blank on a new record.
        formDataToSend.append('vendor', sanitizeOneLine(employeeName));
      }
      if (formData.startDate) formDataToSend.append('start_date', formData.startDate);
      if (formData.endDate) formDataToSend.append('end_date', formData.endDate);
      if (formData.description) formDataToSend.append('description', sanitizeOneLine(formData.description));
      // Document / license number — send for License AND all employee doc types (passport no., permit no., etc.)
      if (documentType === 'License' || isEmployeeDocType(documentType)) {
        formDataToSend.append('license_number', sanitizeOneLine(formData.licenseNumber || ''));
      }
      // Always send value and currency, even if empty (to allow clearing the value)
      formDataToSend.append('value', formData.value || '');
      formDataToSend.append('currency', formData.currency || 'KD');
      if (alertDays && alertDays.length > 0) {
        formDataToSend.append('alert_days', alertDays.map(opt => opt.value).join(','));
      }
      if (notificationEmail) {
        // Clean and format multiple emails (remove extra spaces, normalize)
        const cleanedEmails = notificationEmail.split(',')
          .map(e => e.trim())
          .filter(Boolean)
          .join(',');
        formDataToSend.append('notification_email', cleanedEmails);
      }
      // Add status (for archiving when editing)
      if (contract) {
        formDataToSend.append('status', isArchived ? 'Inactive' : 'Active');
      }
      // Add file if a real File object was selected
      if (isFileLike(file)) {
        formDataToSend.append('file', file);
      }
      // If editing and file was explicitly removed (and no new file uploaded)
      if (contract && formData.fileRemoved && !isFileLike(file)) {
        formDataToSend.append('removeFile', 'true');
      }
      // Always preserve existing attachment path on edit — backend prioritises req.file over this
      if (contract && !formData.fileRemoved) {
        const keepUrl = getEffectiveServerAttachmentUrl(
          contract,
          currentFileUrl,
          !!formData.fileRemoved
        );
        if (keepUrl) {
          formDataToSend.append('file_url', keepUrl);
          formDataToSend.append('fileUrl', keepUrl);
        }
      }
      // If admin, add departmentId
      if (isAdmin && departmentId) {
        formDataToSend.append('departmentId', departmentId);
      }
      // Add document type (Contract or License)
      formDataToSend.append('type', sanitizeOneLine(documentType || 'Contract'));
      // Add region (required)
      formDataToSend.append('region', sanitizeOneLine(region || ''));

      // Debug: dump final FormData so we can confirm what's being sent
      const debugEntries = {};
      formDataToSend.forEach((val, key) => {
        debugEntries[key] = isFileLike(val) ? `File:${val.name}(${val.size}b)` : val;
      });
      console.log('[handleSubmit] FormData being sent:', debugEntries);

      await onSave(formDataToSend);
      onClose();
    } catch (error) {
      console.error('Error saving contract:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || error.message || 'Failed to save contract. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type (PDF, DOC, DOCX, images, etc.)
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      const validExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(selectedFile.type) && !validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          file: 'Please upload a valid document (PDF, DOC, DOCX) or image (JPG, PNG)'
        });
        return;
      }

      // Validate file size (max 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setErrors({
          ...errors,
          file: 'File size must be less than 5MB'
        });
        return;
      }

      setFile(selectedFile);
      setErrors({
        ...errors,
        file: undefined
      });
    }
  };

  const removeFile = () => {
    setFile(null);
    // Mark that the user explicitly removed the file while editing
    if (contract) {
      setFormData(prev => ({
        ...prev,
        fileRemoved: true
      }));
    }
    // Hide the current file from the UI so the upload area is shown
    setCurrentFileUrl('');
  };

  const viewFile = (e) => {
    e.preventDefault();
    if (isFileLike(file)) {
      // For new files that haven't been uploaded yet
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, '_blank');
    } else {
      const serverPath = getEffectiveServerAttachmentUrl(
        contract,
        currentFileUrl,
        !!formData.fileRemoved
      );
      if (serverPath) openStoredFileUrl(serverPath);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange({ target: { files: e.dataTransfer.files } });
    }
  };

  const effectiveServerAttachmentUrl = getEffectiveServerAttachmentUrl(
    contract,
    currentFileUrl,
    !!formData.fileRemoved
  );

  // Diagnostic: log attachment state whenever an employee-type doc is open for editing
  if (isOpen && contract && ['Residence Visa', 'Work Permit', 'Civil ID', 'OHC', 'Passport'].includes(documentType)) {
    console.log('[AddContractModal] DIAG — employee doc attachment state:', {
      documentType,
      contractId: contract?.id,
      'contract.file_url': contract?.file_url,
      'contract.fileUrl': contract?.fileUrl,
      currentFileUrl,
      effectiveServerAttachmentUrl,
      isFileLike: isFileLike(file),
      fileRemoved: formData.fileRemoved,
    });
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-4 sm:my-0">
        <div className="border-b border-gray-200 p-3 sm:p-4 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            {contract ? 'Edit Document' : 'Add New Document'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 flex-shrink-0"
            disabled={isSubmitting}
          >
            <FiX size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {contract && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <span className="font-semibold">Uploaded by:</span>{' '}
                  <span>{contract.created_by_username || contract.owner_username || '—'}</span>
                </div>
                <div>
                  <span className="font-semibold">Uploaded on:</span>{' '}
                  <span>{contract.created_at ? new Date(contract.created_at).toLocaleString() : '—'}</span>
                </div>
                <div>
                  <span className="font-semibold">Last modified by:</span>{' '}
                  <span>{contract.updated_by_username || contract.created_by_username || contract.owner_username || '—'}</span>
                </div>
                <div>
                  <span className="font-semibold">Last modified on:</span>{' '}
                  <span>{contract.updated_at ? new Date(contract.updated_at).toLocaleString() : '—'}</span>
                </div>
              </div>
            </div>
          )}
          {errors.submit && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm flex items-start">
              <FiAlertCircle className="mr-2 mt-0.5 flex-shrink-0" />
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Name */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {documentType === 'Policy' ? 'Policy Name' : isEmployeeDocType(documentType) ? 'Document Name' : 'Contract/License Name'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiFileText className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder={documentType === 'Policy' ? 'e.g., Health Insurance Policy' : isEmployeeDocType(documentType) ? 'e.g., Passport renewal' : 'e.g., Office Lease Agreement'}
                  disabled={isSubmitting}
                />
              </div>
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Document Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                name="documentType"
                value={documentType}
                onChange={(e) => {
                  setDocumentType(e.target.value);
                  // Reset employee name when leaving employee-linked types
                  if (!isEmployeeDocType(normalizeContractTypeClient(e.target.value))) {
                    setEmployeeName('');
                  }
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
                required
              >
                {allowedDocumentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Vendor / Issuer - Only when not an employee-linked document type */}
            {!isEmployeeDocType(documentType) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {documentType === 'Policy' ? 'Issuer' : 'Vendor'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={(e) => {
                      handleChange(e);
                      setShowVendorSuggestions(true);
                    }}
                    onFocus={() => setShowVendorSuggestions(true)}
                    onBlur={() => {
                      // Delay hiding suggestions to allow click on suggestion
                      setTimeout(() => setShowVendorSuggestions(false), 200);
                    }}
                    list="vendor-suggestions"
                    className={`block w-full pl-10 pr-3 py-2 border ${errors.vendor ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    placeholder={documentType === 'Policy' ? 'Issuer name' : 'Vendor name'}
                    disabled={isSubmitting}
                  />
                  <datalist id="vendor-suggestions">
                    {vendorSuggestions.map((vendor, idx) => (
                      <option key={idx} value={vendor} />
                    ))}
                  </datalist>
                  {showVendorSuggestions && formData.vendor && vendorSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                      {vendorSuggestions
                        .filter(vendor =>
                          vendor.toLowerCase().includes(formData.vendor.toLowerCase()) &&
                          vendor.toLowerCase() !== formData.vendor.toLowerCase()
                        )
                        .slice(0, 5)
                        .map((vendor, idx) => (
                          <div
                            key={idx}
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur
                              setFormData(prev => ({ ...prev, vendor }));
                              setShowVendorSuggestions(false);
                            }}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0"
                          >
                            {vendor}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                {errors.vendor && <p className="mt-1 text-sm text-red-600">{errors.vendor}</p>}

                {/* License Number - only for License */}
                {documentType === 'License' && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">
                      License Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      className={`mt-1 block w-full px-3 py-2 border ${errors.licenseNumber ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="Enter license number"
                      disabled={isSubmitting}
                    />
                    {errors.licenseNumber && <p className="mt-1 text-sm text-red-600">{errors.licenseNumber}</p>}
                  </div>
                )}

                {/* Vendor contact number - show only for Contract */}
                {documentType === 'Contract' && (
                  <>
                    <div className="relative mt-2">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <input
                        type="tel"
                        name="vendorContact"
                        value={formData.vendorContact}
                        onChange={handleChange}
                        className={`block w-full pl-10 pr-3 py-2 border ${errors.vendorContact ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Vendor contact number"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.vendorContact && <p className="mt-1 text-sm text-red-600">{errors.vendorContact}</p>}
                  </>
                )}
              </div>
            )}

            {/* Employee Name - Civil ID, OHC, Passport, Residence Visa, Work Permit */}
            {isEmployeeDocType(documentType) && (
              <div className="space-y-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="employeeName"
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-2 border ${errors.employeeName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="Enter full name as on document"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                  {errors.employeeName && <p className="mt-1 text-sm text-red-600">{errors.employeeName}</p>}
                </div>

                {/* Document Number (Passport No., Work Permit No., Visa No., OHC No., Civil ID No.) */}
                <div className="space-y-2 mt-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {documentType === 'Passport' ? 'Passport No.'
                      : documentType === 'Work Permit' ? 'Work Permit No.'
                      : documentType === 'Residence Visa' ? 'Visa No.'
                      : documentType === 'OHC' ? 'OHC No.'
                      : 'Document No.'}
                  </label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={
                      documentType === 'Passport' ? 'Enter passport number'
                      : documentType === 'Work Permit' ? 'Enter work permit number'
                      : documentType === 'Residence Visa' ? 'Enter visa number'
                      : documentType === 'OHC' ? 'Enter OHC number'
                      : 'Enter document number'
                    }
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}

            {/* Start Date / Issuance Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {documentType === 'Policy' ? 'Issuance Date' : 'Start Date'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiCalendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.startDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  disabled={isSubmitting}
                />
              </div>
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>

            {/* End Date / Policy End Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {documentType === 'Policy' ? 'Policy End Date' : 'End Date'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiCalendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate || undefined}
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.endDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  disabled={isSubmitting}
                />
              </div>
              {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
            </div>

            {/* Contract Value - not needed for Policy */}
            {documentType !== 'Policy' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Contract Value
                </label>
                <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    </div>
                    <input
                      type="number"
                      name="value"
                      value={formData.value}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className={`block w-full pl-10 pr-3 py-2 border ${errors.value ? 'border-red-300' : 'border-gray-300'} rounded-l-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="0.00"
                      disabled={isSubmitting}
                    />
                  </div>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="border border-l-0 border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 bg-white"
                    disabled={isSubmitting}
                  >
                    <option value="KD">KD (د.ك)</option>
                    <option value="AED">AED (د.إ)</option>
                    <option value="SAR">SAR (ر.س)</option>
                    <option value="QAR">QAR (ر.ق)</option>
                    <option value="OMR">OMR (ر.ع)</option>
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
                {errors.value && <p className="mt-1 text-sm text-red-600">{errors.value}</p>}
              </div>
            )}

            {/* Region */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Region <span className="text-red-500">*</span>
              </label>
              <select
                name="region"
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  // Clear error when user selects a region
                  if (errors.region) {
                    setErrors(prev => ({
                      ...prev,
                      region: undefined
                    }));
                  }
                }}
                className={`block w-full px-3 py-2 border ${errors.region ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                disabled={isSubmitting}
                required
              >
                <option value="">Select Region</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Dubai">Dubai</option>
                <option value="KSA">KSA</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Oman">Oman</option>
                <option value="Qatar">Qatar</option>
              </select>
              {errors.region && <p className="mt-1 text-sm text-red-600">{errors.region}</p>}
            </div>
          </div>

          {/* Alert days — full width so it is always visible below the main grid (all document types) */}
          <div className="space-y-2 mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700">
              Alert Before Expiry (Days) <span className="text-red-500">*</span>
            </label>
            <Select
              isMulti
              options={ALERT_DAY_OPTIONS}
              value={alertDays}
              onChange={(v) => setAlertDays(Array.isArray(v) ? v : [])}
              isDisabled={isSubmitting}
              placeholder="Select days..."
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 10050 }),
              }}
            />
            <p className="mt-1 text-xs text-gray-500">
              Select one or more days. You can remove by clicking the &apos;x&apos;. Expiry emails use these offsets.
            </p>
            {errors.alertDays && <p className="mt-1 text-sm text-red-600">{errors.alertDays}</p>}
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description (Optional)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.description ? 'border-red-500' : ''}`}
              rows="3"
              placeholder="Enter contract description"
            />
            {errors.description && (
              <p className="text-red-500 text-xs italic">{errors.description}</p>
            )}
          </div>

          {/* File Upload Section */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Contract Document <span className="text-red-500">*</span>
              {contract && effectiveServerAttachmentUrl && (
                <span className="text-xs font-normal text-gray-500 ml-1">
                  (Current: {effectiveServerAttachmentUrl.split('/').pop()})
                </span>
              )}
            </label>

            {(isFileLike(file) || effectiveServerAttachmentUrl) ? (
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0">
                    <FiFile className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate max-w-xs">
                      {isFileLike(file) ? file.name : effectiveServerAttachmentUrl.split('/').pop()}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => document.getElementById('contractFile')?.click()}
                      className="text-gray-600 hover:text-gray-800 focus:outline-none text-sm mr-2"
                      title="Replace file"
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={viewFile}
                      className="text-blue-600 hover:text-blue-800 focus:outline-none"
                      title="View file"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-red-500 hover:text-red-700 focus:outline-none"
                      title="Remove file"
                    >
                      <FiXCircle className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {isFileLike(file) && (
                  <p className="mt-1 text-xs text-gray-500">
                    New file will replace the existing one when saved.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : errors.file ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                    }`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('contractFile')?.click()}
                >
                  <div className="flex flex-col items-center justify-center">
                    <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Drag and drop your file here, or click to browse
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 5MB)
                    </p>
                  </div>
                </div>
                {errors.file && <p className="mt-1 text-sm text-red-600">{errors.file}</p>}
              </div>
            )}
            <input
              id="contractFile"
              type="file"
              className="hidden"
              aria-hidden="true"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
            />
          </div>

          {/* Notification Email */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Notification Email(s) <span className="text-red-500">*</span>
            </label>
            <textarea
              name="notificationEmail"
              value={notificationEmail}
              onChange={e => setNotificationEmail(e.target.value)}
              className={`block w-full border ${errors.notificationEmail ? 'border-red-300' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter email(s) separated by commas (e.g., email1@example.com, email2@example.com)"
              rows={3}
              required
              disabled={isSubmitting}
            />
            {errors.notificationEmail && <p className="mt-1 text-sm text-red-600">{errors.notificationEmail}</p>}
            <p className="text-xs text-gray-500">
              You can enter multiple email addresses separated by commas. All emails will receive expiry notifications.
            </p>
          </div>

          {/* Department Dropdown */}
          {isAdmin && (
            <>
              {console.log('Rendering department dropdown. departments:', departments, 'departmentId:', departmentId)}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={departmentId}
                  onChange={e => {
                    const newDepartmentId = e.target.value;
                    console.log('=== Department select onChange triggered ===');
                    console.log('New department ID:', newDepartmentId);
                    console.log('Departments available:', departments.length);
                    console.log('All departments:', departments.map(d => ({ id: d.id, name: d.name, email: d.email })));

                    // Update department ID first
                    setDepartmentId(newDepartmentId);

                    // No longer auto-fill notification email from department; user enters it manually
                  }}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Archive Option - Only show when editing */}
          {contract && (
            <div className="space-y-2 border-t border-gray-200 pt-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isArchived}
                  onChange={(e) => setIsArchived(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={isSubmitting}
                />
                <span className="text-sm font-medium text-gray-700">
                  Archive this contract (Mark as Inactive)
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-6">
                Archived contracts will be hidden from the default view and can be accessed via the status filter.
              </p>
            </div>
          )}

          {/* Modal Footer */}
          <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {contract ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>{contract ? 'Save Changes' : 'Add Contract'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContractModal;
